import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import axios from "axios";
import api from "../api";

//interface
interface CloudinaryMediaLibraryProps {
  open: boolean;
  onClose: () => void;
  onImagesSelected: (images: CloudinaryImage[]) => void;
  folder?: string;
  title?: string;
  maxSelection?: number;
}

interface CloudinaryImage {
  public_id: string;
  url: string;
  secure_url: string;
  filename?: string;
  thumbnail_url?: string;
  large_url?: string;
  width?: number;
  height?: number;
}

const CloudinaryMediaLibrary = ({
  open,
  onClose,
  onImagesSelected,
  folder = "artworks",
  title = "Select Images from Library",
  maxSelection = 5,
}: CloudinaryMediaLibraryProps) => {
  const [images, setImages] = useState<CloudinaryImage[]>([]);
  const [selectedImages, setSelectedImages] = useState<CloudinaryImage[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [nextCursor, setNextCursor] = useState(null);

  // Fetch images from Cloudinary
  // Make sure we are an administrator
  // Get images in batches of 20 appending to the previous 20 if nrcessary
  const fetchImages = async (cursor = null) => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please log in.");
        return;
      }

      const response = await api.get("/upload/media-library", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          folder,
          max_results: 20,
          next_cursor: cursor,
        },
      });
      console.log("Media library response:", response.data);
      const returnedImages = (
        response.data.data ??
        response.data.images ??
        response.data.resources ??
        []
      ).map((image: any) => ({
        ...image,
        secure_url: image.secure_url ?? image.url,
      }));
      console.log(returnedImages);
      if (cursor) {
        // Append to existing images
        setImages((prev) => [...prev, ...returnedImages]);
      } else {
        // Replace images
        setImages(returnedImages);
      }

      setNextCursor(response.data.next_cursor);
    } catch (error: unknown) {
      console.error("Error fetching images:", error);

      if (axios.isAxiosError(error)) {
        console.error("Error response:", error.response?.data);

        setError(
          error.response?.data?.message || "Failed to load images from library",
        );
      } else {
        setError("Failed to load images from library");
      }
    } finally {
      setLoading(false);
    }
  };

  // Load images when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedImages([]);
      setSearchTerm("");
      fetchImages();
    }
  }, [open, folder]);

  // Search images
  // set the search term, id and filename to lowercase
  // filter the images on the search term or return all images
  const handleSearch = () => {
    if (searchTerm.trim()) {
      // Filter existing images by search term
      const filtered = images.filter(
        (img) =>
          img.public_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          img.filename?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setImages(filtered);
    } else {
      // Reset to all images
      fetchImages();
    }
  };

  // Multi image selection from Cloudinary
  const handleImageSelect = (image: CloudinaryImage) => {
    setSelectedImages((current) => {
      const alreadySelected = current.some(
        (selected) => selected.public_id === image.public_id,
      );

      if (alreadySelected) {
        return current.filter(
          (selected) => selected.public_id !== image.public_id,
        );
      }

      if (current.length >= maxSelection) {
        return current;
      }

      return [...current, image];
    });
  };

  // If we have an image or images
  const handleConfirm = () => {
    if (selectedImages.length > 0) {
      onImagesSelected(selectedImages);
      onClose();
    }
  };

  // Get the next 20 images
  const handleLoadMore = () => {
    if (nextCursor) {
      fetchImages(nextCursor);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "80vh" },
      }}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select up to {maxSelection} image{maxSelection === 1 ? "" : "s"} from
          your Cloudinary library
        </Typography>
      </DialogContent>

      <DialogContent>
        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm("")}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleSearch} variant="outlined" sx={{ mt: 1 }}>
            Search
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        )}

        {/* Images Grid */}
        <Grid container spacing={2}>
          {images.map((image) => {
            const isSelected = selectedImages.some(
              (selected) => selected.public_id === image.public_id,
            );
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.public_id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    position: "relative",
                  }}
                  onClick={() => handleImageSelect(image)}
                >
                  <CardMedia
                    component="img"
                    height="140"
                    image={image.url}
                    alt={image.public_id}
                    sx={{ objectFit: "cover" }}
                  />
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="caption" noWrap>
                      {image.public_id.split("/").pop()}
                    </Typography>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          color: "primary.main",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Load More Button */}
        {nextCursor && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              onClick={handleLoadMore}
              variant="outlined"
              disabled={loading}
            >
              Load More Images
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedImages.length === 0}
        >
          Add {selectedImages.length} Image
          {selectedImages.length === 1 ? "" : "s"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloudinaryMediaLibrary;
